import { Viewer, Cesium3DTileStyle } from "cesium";
import { DeferredValue, Guard } from "lib/actions/util";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { Search } from "lib/cesium/search";
import TilesetPlugin from "./TilesetPlugin";
import {
  ObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";

export type StyleRule =
  | TilesetStyleRule
  | ElementStyleRule
  | ConditionStyleRule;

export type TilesetStyleRule = {
  priority: number;
  tilesetStyle: {
    tilesetId: string;
    color?: string;
    opacity?: number;
  };
};

export type ElementStyleRule = {
  priority: number;
  elementStyle: {
    tilesetId: string;
    elementId: string;
    hidden?: boolean;
    isolated?: boolean;
    color?: string;
    opacity?: number;
  };
};

export type ConditionStyleRule = {
  priority: number;
  conditionStyle: {
    tilesetId?: string;
    condition: string;
    hidden?: boolean;
    isolated?: boolean;
    color?: string;
    opacity?: number;
  };
};

export type StyleRuleIdentifier = {
  priority: number;
  tilesetStyle?: {
    tilesetId: string;
  };
  elementStyle?: {
    tilesetId: string;
    elementId: string;
  };
  conditionStyle?: {
    tilesetId?: string;
  };
};

/**
 * The vector decoder never reads the glTF material, so every polyline starts
 * white unless the style supplies a colour. Content carries each element's base
 * colour as a `displayColour` property; older content has none, and the guard
 * keeps it from handing `color()` an undefined value.
 */
const VECTOR_COLOR_EXPRESSION =
  "${displayColour} === undefined ? color('white') : color(${displayColour})";

/** Screen-space pixel widths the line width tool offers. */
export const VECTOR_LINE_WIDTHS = [1, 2, 3, 5, 8];

export const DEFAULT_VECTOR_LINE_WIDTH = 2;

/** Screen-space pixel size of vector points, which Cesium draws at one pixel. */
const VECTOR_POINT_SIZE = 8;

export const TilesetStylePriority = {
  PaintToolTileset: 100,
  PaintToolElement: 110,
  ElementSearch: 200,
  ElementVisibilityHidden: 300,
  ElementVisibilityHighlighted: 310,
  ElementSelection: 400,
} as const;

export default class TilesetStylingPlugin implements AttachablePluginInterface {
  private readonly _viewer = new DeferredValue<Viewer>();
  private _rules: StyleRule[] = [];
  private readonly _lineWidth = new ObservableProperty(
    DEFAULT_VECTOR_LINE_WIDTH,
  );

  constructor(private readonly _tilesetPlugin: TilesetPlugin) {}

  get lineWidth(): ReadonlyObservableProperty<number> {
    return this._lineWidth;
  }

  /**
   * Setting `tileset.style` replaces the whole style, so the width can only
   * change by rebuilding it: a set followed by an `applyRules` pass.
   */
  setLineWidth(width: number): Promise<void> {
    this._lineWidth.set(width);
    return this.applyRules();
  }

  setViewer(viewer: Viewer): void {
    this._viewer.set(viewer);
  }

  detachViewer(): void {
    this._rules = [];
    this._viewer.clear();
  }

  setRule(rule: StyleRule) {
    this.validateRule(rule);

    const i = this.findRuleIndex(rule);
    if (i == null) this._rules.push(rule);
    else this._rules[i] = rule;
  }

  removeRule(rule: StyleRuleIdentifier) {
    this.validateRuleIdentifier(rule);

    const i = this.findRuleIndex(rule);
    if (i != null) this._rules.splice(i, 1);
  }

  async applyRules() {
    const tilesetRules = this._rules.reduce<Record<string, StyleRule[]>>(
      (acc, r) => {
        let tilesetId;
        if (this.isTilesetStyleRule(r)) {
          tilesetId = r.tilesetStyle.tilesetId;
        } else if (this.isElementStyleRule(r)) {
          tilesetId = r.elementStyle.tilesetId;
        } else {
          tilesetId = r.conditionStyle.tilesetId ?? "global";
        }
        (acc[tilesetId] ??= []).push(r);
        return acc;
      },
      {},
    );
    const tilesets = this._tilesetPlugin
      .getAll()
      .filter(({ tileset }) => !!tileset);
    for (const {
      id: tilesetId,
      tileset,
      isVector,
      parentTilesetId,
    } of tilesets) {
      const rules = [
        ...(tilesetRules[tilesetId] ?? []),
        ...(parentTilesetId ? tilesetRules[parentTilesetId] ?? [] : []),
        ...(tilesetRules["global"] ?? []),
      ];
      rules.sort((a, b) => b.priority - a.priority); //descending priority

      const isIsolationMode = rules.some(
        (r) =>
          (this.isElementStyleRule(r) && r.elementStyle.isolated) ||
          (this.isConditionStyleRule(r) && r.conditionStyle.isolated),
      );
      const showConditions = rules
        .map((rule) => this.createShowCondition(rule, isIsolationMode))
        .filter(Boolean);
      showConditions.push(["true", isIsolationMode ? "false" : "true"]);

      const colorConditions = rules
        .map((rule) => this.createColorCondition(rule))
        .filter(Boolean);
      colorConditions.push([
        "true",
        isVector ? VECTOR_COLOR_EXPRESSION : "color()",
      ]);

      const settings = {
        ...tileset?.style?.style, //existing style settings
        show: undefined,
        color: undefined,
      };
      if (showConditions.length > 1)
        settings.show = { conditions: showConditions };
      if (colorConditions.length > 1 || isVector)
        settings.color = { conditions: colorConditions };
      if (isVector) {
        settings.lineWidth = String(this._lineWidth.getSnapshot());
        settings.pointSize = String(VECTOR_POINT_SIZE);
      }
      Object.keys(settings)
        .filter((key) => settings[key] == undefined)
        .forEach((key) => delete settings[key]); // remove undefined/null settings
      if (Object.keys(settings).length > 0)
        tileset.style = new Cesium3DTileStyle(settings);
      else tileset.style = undefined;
    }
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;
    viewer.scene.requestRender();
  }

  private isTilesetStyleRule(rule: StyleRule): rule is TilesetStyleRule {
    return (rule as TilesetStyleRule).tilesetStyle !== undefined;
  }

  private isElementStyleRule(rule: StyleRule): rule is ElementStyleRule {
    return (rule as ElementStyleRule).elementStyle !== undefined;
  }

  private isConditionStyleRule(rule: StyleRule): rule is ConditionStyleRule {
    return (rule as ConditionStyleRule).conditionStyle !== undefined;
  }

  private validateRule(rule: StyleRule) {
    this.validateRuleIdentifier(rule);
    if (this.isTilesetStyleRule(rule)) {
      if (
        rule.tilesetStyle.opacity != undefined &&
        (rule.tilesetStyle.opacity < 0 || rule.tilesetStyle.opacity > 1)
      )
        throw new Error("Tileset opacity must be >= 0 && <= 1");
    } else if (this.isConditionStyleRule(rule)) {
      if (
        rule.conditionStyle.opacity != undefined &&
        (rule.conditionStyle.opacity < 0 || rule.conditionStyle.opacity > 1)
      )
        throw new Error("Condition opacity must be >= 0 && <= 1");
    } else if (this.isElementStyleRule(rule)) {
      if (
        rule.elementStyle.opacity != undefined &&
        (rule.elementStyle.opacity < 0 || rule.elementStyle.opacity > 1)
      )
        throw new Error("Element opacity must be >= 0 && <= 1");
    } else throw new Error("Missing presence of a supported style property.");
  }

  private validateRuleIdentifier(rule: StyleRuleIdentifier) {
    Guard.require(rule, "rule");
    if (rule.priority == null) throw new Error("Must have a priority.");
    if (isNaN(Number(rule.priority)))
      throw new Error("Priority must be a number.");
    const styleProps = [
      rule.tilesetStyle,
      rule.elementStyle,
      rule.conditionStyle,
    ].filter(Boolean);
    if (styleProps.length !== 1)
      throw new Error(
        "Must have no fewer or greater than one style property set.",
      );
  }

  private findRuleIndex(rule: StyleRuleIdentifier): number | null {
    let i = -1;
    if (rule.tilesetStyle) {
      i = this._rules.findIndex(
        (r) =>
          r.priority === rule.priority &&
          this.isTilesetStyleRule(r) &&
          r.tilesetStyle.tilesetId === rule.tilesetStyle.tilesetId,
      );
    } else if (rule.elementStyle) {
      i = this._rules.findIndex(
        (r) =>
          r.priority === rule.priority &&
          this.isElementStyleRule(r) &&
          r.elementStyle.tilesetId === rule.elementStyle.tilesetId &&
          r.elementStyle.elementId === rule.elementStyle.elementId,
      );
    } else if (rule.conditionStyle) {
      i = this._rules.findIndex(
        (r) =>
          r.priority === rule.priority &&
          this.isConditionStyleRule(r) &&
          (r.conditionStyle.tilesetId ?? null) ===
            (rule.conditionStyle.tilesetId ?? null),
      );
    } else throw new Error("Missing a supported style property.");
    if (i === -1) return null;
    return i;
  }

  private createShowCondition(
    rule: StyleRule,
    isIsolationMode: boolean,
  ): [string, string] | null {
    if (this.isElementStyleRule(rule)) {
      if (!rule.elementStyle.hidden && !rule.elementStyle.isolated) return null;
      return [
        this.createMatchingElementIdExpression(rule.elementStyle.elementId),
        isIsolationMode && rule.elementStyle.isolated ? "true" : "false",
      ];
    } else if (this.isConditionStyleRule(rule)) {
      if (!rule.conditionStyle.hidden && !rule.conditionStyle.isolated)
        return null;
      return [
        rule.conditionStyle.condition,
        isIsolationMode && rule.conditionStyle.isolated ? "true" : "false",
      ];
    }
    return null;
  }

  private createColorCondition(rule: StyleRule): [string, string] | null {
    if (this.isTilesetStyleRule(rule)) {
      if (!rule.tilesetStyle.color) return null;
      return [
        "true",
        `color('${rule.tilesetStyle.color}', ${rule.tilesetStyle.opacity ?? 1})`,
      ];
    } else if (this.isElementStyleRule(rule)) {
      if (!rule.elementStyle.color && rule.elementStyle.opacity == undefined)
        return null;
      return [
        this.createMatchingElementIdExpression(rule.elementStyle.elementId),
        `color('${rule.elementStyle.color ?? "white"}', ${rule.elementStyle.opacity ?? 1})`,
      ];
    } else if (this.isConditionStyleRule(rule)) {
      if (
        !rule.conditionStyle.color &&
        rule.conditionStyle.opacity == undefined
      )
        return null;
      return [
        rule.conditionStyle.condition,
        `color('${rule.conditionStyle.color ?? "white"}', ${rule.conditionStyle.opacity ?? 1})`,
      ];
    }
    return null;
  }

  private createMatchingElementIdExpression(elementId: string) {
    return Search.toCesium3dTileStyleCondition(
      Search.or(Search.eq("id", elementId), Search.eq("GUID", elementId)),
    );
  }
}
